import { Injectable } from "@angular/core";
import { AudioDeviceService } from "../audio-device/audio-device.service";

/**
 * Service for generating audio tones for speaker tests and call state changes
 * Provides acoustic feedback for call events using Web Audio API
 */
@Injectable({
  providedIn: "root",
})
export class ToneService {
  private audioContext: AudioContext | null = null;

  // Ringback tone properties
  private ringbackOscillator: OscillatorNode | null = null;
  private ringbackGainNode: GainNode | null = null;
  private ringbackInterval: any = null;

  // Ringing tone properties
  private ringingOscillators: OscillatorNode[] = [];
  private ringingGainNode: GainNode | null = null;
  private ringingInterval: any = null;
  private ringingAudioElement: HTMLAudioElement | null = null;

  // Call waiting tone properties
  private callWaitingOscillator: OscillatorNode | null = null;
  private callWaitingGainNode: GainNode | null = null;
  private callWaitingInterval: any = null;

  // Busy tone properties
  private busyOscillator: OscillatorNode | null = null;
  private busyGainNode: GainNode | null = null;
  private busyInterval: any = null;
  private busyAutoStopTimeout: any = null;

  constructor(private audioDeviceService: AudioDeviceService) {
    // Initialize Audio Context on demand (not in constructor)
    // to avoid autoplay policy issues
  }

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }

  /**
   * Play a tone for call established (connected)
   * - Rising two-tone sequence to indicate successful connection
   */
  playCallEstablishedTone(): void {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Create gain node for volume control
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0.2; // 20% volume
    gainNode.connect(ctx.destination);

    // First tone (lower)
    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.value = 440; // A4
    osc1.connect(gainNode);

    // Second tone (higher)
    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = 600;
    osc2.connect(gainNode);

    // Schedule playback
    osc1.start(now);
    osc1.stop(now + 0.15);
    osc2.start(now + 0.2);
    osc2.stop(now + 0.35);

    // Clean up
    setTimeout(() => {
      gainNode.disconnect();
    }, 400);
  }

  /**
   * Play a tone for call ended
   * - Falling two-tone sequence to indicate call termination
   */
  playCallEndedTone(): void {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Create gain node for volume control
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0.2; // 20% volume
    gainNode.connect(ctx.destination);

    // First tone (higher)
    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.value = 600;
    osc1.connect(gainNode);

    // Second tone (lower)
    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = 400;
    osc2.connect(gainNode);

    // Schedule playback
    osc1.start(now);
    osc1.stop(now + 0.15);
    osc2.start(now + 0.2);
    osc2.stop(now + 0.35);

    // Clean up
    setTimeout(() => {
      gainNode.disconnect();
    }, 400);
  }

  /**
   * Play a tone for call muted
   * - Two distinct ascending tones
   */
  playCallMutedTone(): void {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Create gain node for volume control
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0.15; // 15% volume
    gainNode.connect(ctx.destination);

    // First tone (lower)
    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.value = 600;
    osc1.connect(gainNode);

    // Second tone (higher)
    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = 700;
    osc2.connect(gainNode);

    // Schedule playback with slight overlap
    osc1.start(now);
    osc1.stop(now + 0.12);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.22);

    // Clean up
    setTimeout(() => {
      gainNode.disconnect();
    }, 300);
  }

  /**
   * Play a tone for call unmuted
   * - Two distinct descending tones
   */
  playCallUnmutedTone(): void {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Create gain node for volume control
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0.15; // 15% volume
    gainNode.connect(ctx.destination);

    // First tone (higher)
    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.value = 700;
    osc1.connect(gainNode);

    // Second tone (lower)
    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = 500;
    osc2.connect(gainNode);

    // Schedule playback with slight overlap
    osc1.start(now);
    osc1.stop(now + 0.12);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.22);

    // Clean up
    setTimeout(() => {
      gainNode.disconnect();
    }, 300);
  }

  /**
   * Play a tone for call put on hold
   * - Two short, low beeps
   */
  playCallHeldTone(): void {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Create gain node for volume control
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0.15; // 15% volume
    gainNode.connect(ctx.destination);

    // Create oscillator for first beep
    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.value = 500; // Lower tone
    osc1.connect(gainNode);

    // Create oscillator for second beep
    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = 500; // Same as first
    osc2.connect(gainNode);

    // Schedule playback
    osc1.start(now);
    osc1.stop(now + 0.1);
    osc2.start(now + 0.2);
    osc2.stop(now + 0.3);

    // Clean up
    setTimeout(() => {
      gainNode.disconnect();
    }, 400);
  }

  /**
   * Play a tone for call resumed from hold
   * - Two short, rising beeps
   */
  playCallResumedTone(): void {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Create gain node for volume control
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0.15; // 15% volume
    gainNode.connect(ctx.destination);

    // Create oscillator for first beep
    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.value = 500; // Lower tone
    osc1.connect(gainNode);

    // Create oscillator for second beep
    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = 600; // Higher tone
    osc2.connect(gainNode);

    // Schedule playback
    osc1.start(now);
    osc1.stop(now + 0.1);
    osc2.start(now + 0.15);
    osc2.stop(now + 0.25);

    // Clean up
    setTimeout(() => {
      gainNode.disconnect();
    }, 350);
  }

  /**
   * Play a test tone for speaker testing
   * - Modeled after the ringback tone (425 Hz, similar pattern)
   */
  async playSpeakerTestTone(): Promise<void> {
    try {
      const ctx = this.getAudioContext();
      const dest = ctx.createMediaStreamDestination();

      // Create audio element that will play on the selected device
      const audioElement = new Audio();

      // Apply the selected output device
      await this.audioDeviceService.applyAudioOutputDevice(audioElement);

      // Create gain node for volume control
      const masterGain = ctx.createGain();
      masterGain.gain.value = 0.2; // 20% volume
      masterGain.connect(dest);

      // Create oscillator using same frequency as ringback (425 Hz)
      const osc1 = ctx.createOscillator();
      osc1.type = "sine";
      osc1.frequency.value = 425; // Same as ringback tone
      osc1.connect(masterGain);

      // Add a second pulse after a brief pause (similar to ringback pattern)
      const osc2 = ctx.createOscillator();
      osc2.type = "sine";
      osc2.frequency.value = 425;
      osc2.connect(masterGain);

      // Start tones
      const now = ctx.currentTime;
      osc1.start(now);
      osc1.stop(now + 0.5);
      // Schedule the second tone with a short gap
      osc2.start(now + 0.7);
      osc2.stop(now + 1.2); // Also half a second on

      // Connect media stream to audio element and play
      audioElement.srcObject = dest.stream;
      audioElement.play();

      // Clean up
      setTimeout(() => {
        masterGain.disconnect();
        audioElement.pause();
        if (audioElement.srcObject) {
          audioElement.srcObject = null;
        }
      }, 1300);
    } catch (error) {
      console.error("Error playing speaker test tone:", error);
    }
  }

  /**
   * Starts playing the ringback tone (German pattern: 425 Hz, 1s on 4s off)
   */
  startRingbackTone(): void {
    // Stop any existing ringback tone
    this.stopRingbackTone();

    try {
      const ctx = this.getAudioContext();

      // Create gain node for volume control
      this.ringbackGainNode = ctx.createGain();
      this.ringbackGainNode.gain.value = 0.2; // Set volume to 20%
      this.ringbackGainNode.connect(ctx.destination);

      // Create and start a tone
      const playTone = () => {
        // Create oscillator
        this.ringbackOscillator = ctx.createOscillator();
        this.ringbackOscillator.type = "sine";
        this.ringbackOscillator.frequency.value = 425;

        // Connect to gain node
        this.ringbackOscillator.connect(this.ringbackGainNode!);

        // Start tone
        this.ringbackOscillator.start();

        // Stop tone after 1 second
        setTimeout(() => {
          if (this.ringbackOscillator) {
            this.ringbackOscillator.stop();
            this.ringbackOscillator.disconnect();
            this.ringbackOscillator = null;
          }
        }, 1000);
      };

      // Play the first tone immediately
      playTone();

      // Set up interval to repeat the pattern (every 5 seconds)
      this.ringbackInterval = setInterval(playTone, 5000); // 1s on + 4s off = 5s cycle

      console.log("Started ringback tone");
    } catch (error) {
      console.error("Error starting ringback tone:", error);
    }
  }

  /**
   * Stops the ringback tone
   */
  stopRingbackTone(): void {
    // Clear interval
    if (this.ringbackInterval) {
      clearInterval(this.ringbackInterval);
      this.ringbackInterval = null;
    }

    // Stop oscillator if active
    if (this.ringbackOscillator) {
      try {
        this.ringbackOscillator.stop();
        this.ringbackOscillator.disconnect();
      } catch (e) {
        // Ignore errors if oscillator is already stopped
      }
      this.ringbackOscillator = null;
    }

    // Disconnect gain node
    if (this.ringbackGainNode) {
      try {
        this.ringbackGainNode.disconnect();
      } catch (e) {
        // Ignore errors
      }
      this.ringbackGainNode = null;
    }

    console.log("Stopped ringback tone");
  }

  /**
   * Starts playing a gentle, pleasant melodic ringing tone for incoming calls
   * - Uses softer waveforms and lower frequencies for a calming effect
   * - Implements a professional-sounding pattern with gradual volume transitions
   */
  startRingingTone(): void {
    // Stop any existing ringing tone
    this.stopRingingTone();

    try {
      // Create a Web Audio context for generating the tone
      const ctx = this.getAudioContext();
      const dest = ctx.createMediaStreamDestination();

      // Create an audio element that will play the ringing tone
      // on the selected ringing device
      this.ringingAudioElement = new Audio();

      // Apply the selected ringing device to the audio element
      this.audioDeviceService
        .applyRingingDevice(this.ringingAudioElement)
        .then(() => {
          // Once the device is set, create the actual tone
          this.createRingingToneOnDevice(ctx, dest);
        })
        .catch((error) => {
          console.error("Error applying ringing device:", error);
          // Fall back to normal Web Audio playback if device selection fails
          this.createRingingToneOnDevice(ctx);
        });
    } catch (error) {
      console.error("Error starting ringing tone:", error);
    }
  }

  /**
   * Creates the ringing tone using Web Audio API
   * @param ctx The audio context
   * @param dest Optional media stream destination for device output
   */
  private createRingingToneOnDevice(
    ctx: AudioContext,
    dest?: MediaStreamAudioDestinationNode,
  ): void {
    // Use the destination if provided, otherwise use the default destination
    const destination = dest || ctx.destination;

    // Create master gain node for volume control
    this.ringingGainNode = ctx.createGain();
    this.ringingGainNode.gain.value = 0.2; // Reduced to 20% volume for gentleness
    this.ringingGainNode.connect(destination);

    // Define musical notes for a gentler, more soothing melody
    // Based on a softer pentatonic scale with lower frequencies
    const notes = [
      { frequency: 587.33, duration: 0.2 }, // D5
      { frequency: 659.25, duration: 0.2 }, // E5
      { frequency: 783.99, duration: 0.3 }, // G5
      { frequency: 0, duration: 0.15 }, // Longer pause
      { frequency: 659.25, duration: 0.2 }, // E5
      { frequency: 587.33, duration: 0.3 }, // D5
    ];

    const playMelody = () => {
      // Clear previous oscillators array
      this.ringingOscillators = [];

      let startTime = ctx.currentTime;
      let totalDuration = 0;

      // Create and schedule oscillators for each note
      notes.forEach((note) => {
        // Skip for pause (frequency = 0)
        if (note.frequency > 0) {
          // Create oscillator for this note
          const osc = ctx.createOscillator();

          // Use sine wave for the gentlest, most pleasant tone
          osc.type = "sine";
          osc.frequency.value = note.frequency;

          // Create individual gain node for envelope shaping
          const noteGain = ctx.createGain();

          // Connect oscillator -> noteGain -> master gain
          osc.connect(noteGain);
          noteGain.connect(this.ringingGainNode!);

          // Shape the envelope with longer attack and release for softer transitions
          const attackTime = 0.04; // Longer attack
          const releaseTime = 0.08; // Longer release
          const holdTime = note.duration - attackTime - releaseTime;

          // Softer envelope shaping
          noteGain.gain.setValueAtTime(0, startTime);
          noteGain.gain.linearRampToValueAtTime(0.6, startTime + attackTime); // Lower peak volume
          noteGain.gain.setValueAtTime(0.6, startTime + attackTime + holdTime);
          noteGain.gain.linearRampToValueAtTime(0, startTime + note.duration);

          // Schedule oscillator
          osc.start(startTime);
          osc.stop(startTime + note.duration);

          // Add to oscillators array for cleanup
          this.ringingOscillators.push(osc);
        }

        // Advance the start time for the next note
        startTime += note.duration;
        totalDuration += note.duration;
      });

      // Add two harmonic layers for a richer but gentler tone
      if (this.ringingGainNode) {
        // First harmonic - one octave lower
        const harmonicOsc1 = ctx.createOscillator();
        harmonicOsc1.type = "sine";
        harmonicOsc1.frequency.value = 293.66; // D4

        const harmonicGain1 = ctx.createGain();
        harmonicGain1.gain.value = 0.1; // Subtle undertone

        harmonicOsc1.connect(harmonicGain1);
        harmonicGain1.connect(this.ringingGainNode);

        // Second harmonic - fifth above base note for richness
        const harmonicOsc2 = ctx.createOscillator();
        harmonicOsc2.type = "sine";
        harmonicOsc2.frequency.value = 440; // A4

        const harmonicGain2 = ctx.createGain();
        harmonicGain2.gain.value = 0.08; // Very subtle

        harmonicOsc2.connect(harmonicGain2);
        harmonicGain2.connect(this.ringingGainNode);

        // Start and stop both harmonics
        harmonicOsc1.start(ctx.currentTime);
        harmonicOsc1.stop(ctx.currentTime + totalDuration);
        harmonicOsc2.start(ctx.currentTime);
        harmonicOsc2.stop(ctx.currentTime + totalDuration);

        // Add to oscillators array for cleanup
        this.ringingOscillators.push(harmonicOsc1, harmonicOsc2);
      }

      // Schedule cleanup after the melody completes
      setTimeout(
        () => {
          this.ringingOscillators.forEach((osc) => {
            try {
              osc.disconnect();
            } catch (e) {
              // Ignore errors if already disconnected
            }
          });
          this.ringingOscillators = [];
        },
        totalDuration * 1000 + 100,
      );

      // If we're using an audio element with the selected device,
      // connect the media stream from the destination to it
      if (dest && this.ringingAudioElement) {
        this.ringingAudioElement.srcObject = dest.stream;
        this.ringingAudioElement.loop = true;
        this.ringingAudioElement.play().catch((err) => {
          console.error("Error playing ringing tone on selected device:", err);
        });
      }
    };

    // Play the melody immediately
    playMelody();

    // Set up interval to repeat the pattern with a longer gap between repetitions
    // Total duration of melody ~1.35s + 1.15s gap = 2.5s cycle (slower repeat)
    this.ringingInterval = setInterval(playMelody, 2500);

    console.log("Started ringing tone");
  }

  /**
   * Stops the ringing tone
   */
  stopRingingTone(): void {
    // Clear interval
    if (this.ringingInterval) {
      clearInterval(this.ringingInterval);
      this.ringingInterval = null;
    }

    // Stop all oscillators
    this.ringingOscillators.forEach((osc) => {
      try {
        osc.stop();
        osc.disconnect();
      } catch (e) {
        // Ignore errors if oscillator is already stopped
      }
    });
    this.ringingOscillators = [];

    // Disconnect gain node
    if (this.ringingGainNode) {
      try {
        this.ringingGainNode.disconnect();
      } catch (e) {
        // Ignore errors
      }
      this.ringingGainNode = null;
    }

    // Stop the audio element
    if (this.ringingAudioElement) {
      this.ringingAudioElement.pause();
      if (this.ringingAudioElement.srcObject) {
        this.ringingAudioElement.srcObject = null;
      }
      this.ringingAudioElement = null;
    }

    console.log("Stopped ringing tone");
  }

  /**
   * Starts playing a subtle call waiting tone (single beep pattern)
   * - Uses a gentle, professional tone that doesn't interfere with active calls
   * - Repeats every 10 seconds to subtly remind of the waiting call
   */
  startCallWaitingTone(): void {
    // Stop any existing call waiting tone
    this.stopCallWaitingTone();

    try {
      const ctx = this.getAudioContext();

      // Create gain node for volume control - lower volume for call waiting
      this.callWaitingGainNode = ctx.createGain();
      this.callWaitingGainNode.gain.value = 0.12; // Even lower volume (12%) to not interfere with call
      this.callWaitingGainNode.connect(ctx.destination);

      // Create and start a tone
      const playCallWaitingBeep = () => {
        // Create oscillator for a single, gentle beep
        this.callWaitingOscillator = ctx.createOscillator();
        this.callWaitingOscillator.type = "sine";
        this.callWaitingOscillator.frequency.value = 800; // Higher frequency to distinguish from call audio

        // Create envelope gain for smoother tone
        const envelopeGain = ctx.createGain();
        envelopeGain.gain.value = 0;

        // Connect: oscillator -> envelope -> master gain
        this.callWaitingOscillator.connect(envelopeGain);
        envelopeGain.connect(this.callWaitingGainNode!);

        // Create gentle envelope (attack and release)
        const now = ctx.currentTime;
        const toneDuration = 0.2; // Short 200ms beep
        const attackTime = 0.05;
        const releaseTime = 0.05;

        envelopeGain.gain.setValueAtTime(0, now);
        envelopeGain.gain.linearRampToValueAtTime(1, now + attackTime);
        envelopeGain.gain.setValueAtTime(1, now + toneDuration - releaseTime);
        envelopeGain.gain.linearRampToValueAtTime(0, now + toneDuration);

        // Start tone
        this.callWaitingOscillator.start(now);

        // Stop tone after duration
        setTimeout(() => {
          if (this.callWaitingOscillator) {
            try {
              this.callWaitingOscillator.stop();
              this.callWaitingOscillator.disconnect();
            } catch (e) {
              // Ignore errors if already stopped
            }
            this.callWaitingOscillator = null;
          }
        }, toneDuration * 1000);
      };

      // Play the first beep immediately
      playCallWaitingBeep();

      // Set up interval to repeat every 10 seconds (longer interval for subtlety)
      this.callWaitingInterval = setInterval(playCallWaitingBeep, 10000);

      console.log("Started call waiting tone");
    } catch (error) {
      console.error("Error starting call waiting tone:", error);
    }
  }

  /**
   * Stops the call waiting tone
   */
  stopCallWaitingTone(): void {
    // Clear interval
    if (this.callWaitingInterval) {
      clearInterval(this.callWaitingInterval);
      this.callWaitingInterval = null;
    }

    // Stop oscillator if active
    if (this.callWaitingOscillator) {
      try {
        this.callWaitingOscillator.stop();
        this.callWaitingOscillator.disconnect();
      } catch (e) {
        // Ignore errors if oscillator is already stopped
      }
      this.callWaitingOscillator = null;
    }

    // Disconnect gain node
    if (this.callWaitingGainNode) {
      try {
        this.callWaitingGainNode.disconnect();
      } catch (e) {
        // Ignore errors
      }
      this.callWaitingGainNode = null;
    }

    console.log("Stopped call waiting tone");
  }

  /**
   * Starts playing the busy tone (German pattern: 425 Hz, 480ms on / 480ms off)
   * Auto-stops after the specified duration.
   * @param autoStopMs Duration in ms after which the tone stops automatically (default: 3000)
   */
  startBusyTone(autoStopMs: number = 3000): void {
    this.stopBusyTone();

    try {
      const ctx = this.getAudioContext();

      this.busyGainNode = ctx.createGain();
      this.busyGainNode.gain.value = 0.2;
      this.busyGainNode.connect(ctx.destination);

      const playTone = () => {
        this.busyOscillator = ctx.createOscillator();
        this.busyOscillator.type = "sine";
        this.busyOscillator.frequency.value = 425;

        this.busyOscillator.connect(this.busyGainNode!);
        this.busyOscillator.start();

        setTimeout(() => {
          if (this.busyOscillator) {
            this.busyOscillator.stop();
            this.busyOscillator.disconnect();
            this.busyOscillator = null;
          }
        }, 480);
      };

      playTone();

      // 480ms on + 480ms off = 960ms cycle
      this.busyInterval = setInterval(playTone, 960);

      this.busyAutoStopTimeout = setTimeout(() => {
        this.stopBusyTone();
      }, autoStopMs);

      console.log("Started busy tone");
    } catch (error) {
      console.error("Error starting busy tone:", error);
    }
  }

  /**
   * Stops the busy tone
   */
  stopBusyTone(): void {
    if (this.busyAutoStopTimeout) {
      clearTimeout(this.busyAutoStopTimeout);
      this.busyAutoStopTimeout = null;
    }

    if (this.busyInterval) {
      clearInterval(this.busyInterval);
      this.busyInterval = null;
    }

    if (this.busyOscillator) {
      try {
        this.busyOscillator.stop();
        this.busyOscillator.disconnect();
      } catch (e) {
        // Ignore errors if oscillator is already stopped
      }
      this.busyOscillator = null;
    }

    if (this.busyGainNode) {
      try {
        this.busyGainNode.disconnect();
      } catch (e) {
        // Ignore errors
      }
      this.busyGainNode = null;
    }
  }

  /**
   * Standard DTMF frequency map (ITU-T Q.23)
   * Each digit maps to a [lowFrequency, highFrequency] pair
   */
  private static readonly DTMF_FREQUENCIES: Record<string, [number, number]> = {
    "1": [697, 1209],
    "2": [697, 1336],
    "3": [697, 1477],
    "4": [770, 1209],
    "5": [770, 1336],
    "6": [770, 1477],
    "7": [852, 1209],
    "8": [852, 1336],
    "9": [852, 1477],
    "0": [941, 1336],
    "*": [941, 1209],
    "#": [941, 1477],
  };

  /**
   * Play a DTMF tone for local audio feedback
   * Uses standard ITU-T dual-tone frequencies
   * @param digit The DTMF digit (0-9, *, #)
   */
  playDtmfTone(digit: string): void {
    const frequencies = ToneService.DTMF_FREQUENCIES[digit];
    if (!frequencies) {
      return;
    }

    try {
      const ctx = this.getAudioContext();
      if (ctx.state === "suspended") {
        ctx.resume();
      }
      const now = ctx.currentTime;
      const duration = 0.15; // 150ms — short, snappy feedback

      const gainNode = ctx.createGain();
      gainNode.gain.value = 0.15;
      gainNode.connect(ctx.destination);

      const oscLow = ctx.createOscillator();
      oscLow.type = "sine";
      oscLow.frequency.value = frequencies[0];
      oscLow.connect(gainNode);

      const oscHigh = ctx.createOscillator();
      oscHigh.type = "sine";
      oscHigh.frequency.value = frequencies[1];
      oscHigh.connect(gainNode);

      oscLow.start(now);
      oscLow.stop(now + duration);
      oscHigh.start(now);
      oscHigh.stop(now + duration);

      setTimeout(
        () => {
          gainNode.disconnect();
        },
        duration * 1000 + 50,
      );
    } catch (error) {
      console.error("Error playing DTMF tone:", error);
    }
  }

  /**
   * Close audio context when service is destroyed
   */
  dispose(): void {
    // Stop any ongoing tones
    this.stopRingbackTone();
    this.stopRingingTone();
    this.stopCallWaitingTone();
    this.stopBusyTone();

    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close();
    }
    this.audioContext = null;
  }
}
